import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, useTheme } from '@mui/material';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import SecondaryLoader from '../loaders/SecondaryLoader';

export default function ReserveBody() {
  const theme = useTheme();
  const navigate = useNavigate();

  // Image URLs (retained as originally provided)
  const patternUrl = "https://brokepiggytoken.xyz/wp-content/uploads/2025/07/OlivegrovePuglia.jpeg";
  const secondImgUrl = "https://brokepiggytoken.xyz/wp-content/uploads/2025/07/filteredoil-reserve-1080-x-1080-px.png";
  const thirdImgUrl = "https://brokepiggytoken.xyz/wp-content/uploads/2025/07/olives-on-ground-scaled.jpg";
  const headerDesign = 'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/from-marrakech-with-heritage-8.png';
  const headerDesignWhite = 'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/from-marrakech-with-heritage-7-scaled.png';
  const accent = '#b0a990';

  // Loader state
  const [loading, setLoading] = useState(false);

  // Intersection animation
  const controls = useAnimation();
  const { ref, inView } = useInView({ once: true, threshold: 0.3 });
  useEffect(() => {
    if (inView) controls.start('visible');
  }, [controls, inView]);

  const textVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { delay: 0.4, duration: 0.7 } }
  };

  const handleClick = () => {
    setLoading(true);
    setTimeout(() => {
      navigate('/reserve-experience');
    }, 2000);
  };

  return (
    <>
      <AnimatePresence>
        {loading && <SecondaryLoader key="loader" />}
      </AnimatePresence>

      {/* Intro Section */}
      <Box
        id="reserve-intro"
        ref={ref}
        sx={{
          position: 'relative',
          width: '100%',
          bgcolor: 'common.white',
          color: 'common.black',
          py: { xs: 5, md: 8 },
          overflow: 'hidden',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            alignItems: 'center',
            gap: { xs: 3, md: 6 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: { xs: 180, md: 360 },
              backgroundImage: `url(${patternUrl})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          />

          <motion.div
            variants={textVariants}
            initial="hidden"
            animate={controls}
            style={{ flex: 1 }}
          >
            <Box
              component="img"
              src={headerDesign}
              alt="From Marrakech with Heritage"
              sx={{ width: { xs: 140, md: 180 }, mb: 3 }}
            />

            <Box sx={{ width: 80, height: 5, bgcolor: accent, mb: 2, borderRadius: 2 }} />

            <Typography
              variant="body1"
              sx={{ mb: 3, fontSize: { xs: '0.96rem', md: '1.05rem' }, lineHeight: 1.7 }}
            >
              Hidden beyond the ochre city walls of Marrakech, our private Reserve spans 120 acres of fertile, red-hued earth. Each olive tree here stands for over a century—rooted in lore and nurtured by our family's devoted hands.
            </Typography>

            <Typography
              variant="body1"
              sx={{ mb: 3, fontSize: { xs: '0.96rem', md: '1.05rem' }, lineHeight: 1.7 }}
            >
              This secluded sanctuary yields an ultra-limited harvest. Between the gnarled trunks, herbs and wildflowers flourish, infusing the olives with subtle notes that define our Reserve’s singular character.
            </Typography>

            <Box
              component="button"
              onClick={handleClick}
              style={{ all: 'unset', cursor: 'pointer' }}
            >
          
            </Box>
          </motion.div>
        </Container>

        <Box
          sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 100, background: 'linear-gradient(rgba(255,255,255,0), #fff)' }}
        />
      </Box>

      {/* Harvest Section */}
      <Box
        id="reserve-harvest"
        ref={ref}
        sx={{
          position: 'relative',
          width: '100%',
          bgcolor: 'common.black',
          color: 'common.white',
          py: { xs: 5, md: 8 },
          overflow: 'hidden',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, alignItems: 'center', gap: { xs: 3, md: 6 } }}
        >
          <motion.div
            variants={textVariants}
            initial="hidden"
            animate={controls}
            style={{ flex: 1 }}
          >
            <Box
              component="img"
              src={headerDesignWhite}
              alt="From Marrakech with Heritage"
              sx={{ width: { xs: 140, md: 180 }, mb: 3 }}
            />

            <Box sx={{ width: 80, height: 5, bgcolor: accent, mb: 2, borderRadius: 2 }} />

            <Typography
              variant="body1"
              sx={{ color: 'common.white', mb: 3, fontSize: { xs: '0.96rem', md: '1.05rem' }, lineHeight: 1.7 }}
            >
              Our harvest unfolds with the spring equinox. At first light, we hand-select olives at peak ripeness, gathering only the finest pods from shaded branches—in micro-lots that reflect the Reserve’s exclusivity.
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: 'common.white', mb: 3, fontSize: { xs: '0.96rem', md: '1.05rem' }, lineHeight: 1.7 }}
            >
              Within hours, olives arrive at our private stone-mill courtyard. Cold-pressed below 25 °C, each drop preserves volatile aromas—hints of date blossom, sun-baked clay, and a peppery finish that lingers like a desert breeze.
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: 'common.white', mb: 3, fontSize: { xs: '0.96rem', md: '1.05rem' }, lineHeight: 1.7 }}
            >
              Limited to a few hundred liters each year, our Reserve oil graces the tables of discerning gastronomes. Every bottle is a liquid heirloom, carrying the spirit of Marrakech from our family to yours.
            </Typography>

            <Box
              component="button"
              onClick={handleClick}
              style={{ all: 'unset', cursor: 'pointer' }}
            >
      
            </Box>
          </motion.div>

          <Box
            sx={{
              flex: 1,
              height: { xs: 180, md: 360 },
              backgroundImage: `url(${thirdImgUrl})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          />
        </Container>
      </Box>
    </>
  );
}
